<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Let's get your practice texting-ready.</h1>
    <p class="a2p-sub">
      Before your practice can send appointment reminders and care updates by text,
      US and Canadian regulations require carriers (AT&T, Verizon, T-Mobile, Rogers)
      to verify your business. We handle the filing — you answer eleven questions
      and post two compliance blocks on your website. Takes about 15 minutes.
    </p>

    <!-- Overview cards -->
    <div class="a2p-overview-grid">
      <div class="a2p-overview-card">
        <div class="ov-num">01</div>
        <div class="ov-title">Business information</div>
        <div class="ov-desc">Seven fields we need to register your entity with the carriers.</div>
        <div class="ov-status">Pending</div>
      </div>
      <div class="a2p-overview-card">
        <div class="ov-num">02</div>
        <div class="ov-title">Authorized representative</div>
        <div class="ov-desc">Verified against public business records — the single most common cause of rejection.</div>
        <div class="ov-status">Pending</div>
      </div>
      <div class="a2p-overview-card">
        <div class="ov-num">03</div>
        <div class="ov-title">Website compliance</div>
        <div class="ov-desc">Two copy-paste blocks to add to your privacy policy and terms pages.</div>
        <div class="ov-status">Pending</div>
      </div>
      <div class="a2p-overview-card">
        <div class="ov-num">04</div>
        <div class="ov-title">What happens next</div>
        <div class="ov-desc">We take it from here — screenshots, submission, approval.</div>
        <div class="ov-status">Reference</div>
      </div>
    </div>

    <!-- Timeline -->
    <div class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">What happens next</div>
        <h2>From submission to live SMS</h2>
      </div>
      <div class="a2p-timeline">
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">1</div>
          <div class="a2p-timeline-body">
            <h4>You send us this form</h4>
            <p>Answer the eleven questions below and post the two compliance blocks on your website.</p>
            <div class="a2p-timeline-meta">You · ~15 min</div>
          </div>
        </div>
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">2</div>
          <div class="a2p-timeline-body">
            <h4>Automated compliance check</h4>
            <p>We scan your site for the required SMS language. If anything's off, we email exactly what to fix.</p>
            <div class="a2p-timeline-meta">Us · same day</div>
          </div>
        </div>
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">3</div>
          <div class="a2p-timeline-body">
            <h4>Opt-in screenshots generated</h4>
            <p>We pull the SMS consent form from inside your portal — no work on your end.</p>
            <div class="a2p-timeline-meta">Us · same day</div>
          </div>
        </div>
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">4</div>
          <div class="a2p-timeline-body">
            <h4>Registration submitted</h4>
            <p>We file with our messaging vendor and monitor through carrier approval.</p>
            <div class="a2p-timeline-meta">Us · day 1</div>
          </div>
        </div>
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">5</div>
          <div class="a2p-timeline-body">
            <h4>Carrier approval</h4>
            <p>Typically 2–4 days for a clean submission. Up to 20–25 if rejections need fixing.</p>
            <div class="a2p-timeline-meta">Carriers · 2–25 days</div>
          </div>
        </div>
        <div class="a2p-timeline-item">
          <div class="a2p-timeline-num">6</div>
          <div class="a2p-timeline-body">
            <h4>Texting number purchased</h4>
            <p>Your practice is live for SMS.</p>
            <div class="a2p-timeline-meta">Us · same day as approval</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Super Admin — quick import from pasted email -->
    <div v-if="isSuperAdmin" class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Super admin shortcut</div>
        <h2>Quick import</h2>
        <p class="a2p-section-desc">
          Paste the client's email here to auto-fill the form. Saves you the manual
          typing for fields the client has already provided.
        </p>
      </div>
      <A2PCallout v-if="isCountryMissingFromDB" type="warn">
        <strong>Country not set in database.</strong> Defaulting to US. Verify the
        country in Business Info or update the sites table.
      </A2PCallout>
      <A2PCallout v-else type="info">
        Detected country: <strong>{{ countryDisplay }}</strong> (from sites table).
      </A2PCallout>

      <div class="a2p-field" style="background: var(--a2p-surface-2)">
        <textarea
          v-model="emailContent"
          class="a2p-field-textarea"
          rows="7"
          :placeholder="`Paste the client's email here…\n\nExample:\nLegal business name: ABC Dental PLLC\nEIN: 12-3456789\nBusiness type: LLC\nBusiness address: 123 Main St, City, ST 12345\nContact person: John Smith, Owner\nEmail: jsmith@abcdental.com\nPhone: 555-123-4567\nWebsite: www.abcdental.com`"
          :disabled="extracting"
        />
        <div style="display:flex; gap:10px; margin-top: 12px;">
          <button
            type="button"
            class="a2p-btn a2p-btn-primary"
            @click="extractFromEmail"
            :disabled="!emailContent.trim() || extracting"
          >
            {{ extracting ? 'Extracting…' : 'Extract & auto-fill' }}
          </button>
          <button
            type="button"
            class="a2p-btn a2p-btn-secondary"
            @click="emailContent = ''"
            :disabled="!emailContent.trim() || extracting"
          >
            Clear
          </button>
        </div>
      </div>

      <div v-if="hasExtractedData" class="a2p-callout success" style="flex-direction: column; align-items: stretch;">
        <div>
          <strong>Extracted the following fields:</strong>
          <div style="margin-top: 12px; display: grid; grid-template-columns: 140px 1fr; gap: 6px 16px; font-size: 13px;">
            <template v-for="item in extractedDataList">
              <div :key="'k-' + item.key" style="color: var(--a2p-fg-3)">{{ item.label }}</div>
              <div :key="'v-' + item.key" style="font-weight: 500">{{ item.value }}</div>
            </template>
          </div>
        </div>
        <div v-if="missingRequiredFields.length > 0" style="margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--a2p-border)">
          <strong>Missing from the email:</strong>
          <ul style="margin: 6px 0 0 18px; padding: 0; font-size: 13px;">
            <li v-for="f in missingRequiredFields" :key="f.key">{{ f.label }}</li>
          </ul>
          <p style="margin: 8px 0 0; font-size: 12px; color: var(--a2p-fg-3);">
            You can still proceed — missing fields will need to be filled manually.
          </p>
        </div>
        <div style="margin-top: 14px;">
          <button type="button" class="a2p-btn a2p-btn-primary" @click="applyExtractedData">
            Apply & continue
          </button>
        </div>
      </div>
    </div>

    <div class="a2p-actions">
      <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('close')">
        Cancel
      </button>
      <button type="button" class="a2p-btn a2p-btn-primary" @click="$emit('next')">
        Start the form
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import A2PCallout from './components/A2PCallout.vue';

export default {
  name: 'A2PWizardIntro',
  components: { A2PCallout },
  props: {
    eyebrow: { type: String, default: '' },
    siteCountry: { type: String, default: 'US' },
    siteCountryFromDB: { type: String, default: null },
  },
  data() {
    return {
      emailContent: '',
      extracting: false,
      extractedData: null,
    };
  },
  computed: {
    ...mapState('customer', ['user']),
    isSuperAdmin() {
      return this.user?.Role === 'Super Admin';
    },
    hasExtractedData() {
      return this.extractedData && Object.keys(this.extractedData).some(k => this.extractedData[k]);
    },
    extractedDataList() {
      if (!this.extractedData) return [];
      const labels = {
        businessName: 'Business name',
        businessAddress: 'Address',
        ein: 'EIN/Tax ID',
        businessType: 'Business type',
        website: 'Website',
        repName: 'Contact name',
        repTitle: 'Title',
        repEmail: 'Email',
        repPhone: 'Phone',
        privacyPolicyURL: 'Privacy policy URL',
        termsOfServiceURL: 'Terms of service URL',
      };
      return Object.entries(this.extractedData)
        .filter(([, v]) => v)
        .map(([k, v]) => ({ key: k, label: labels[k] || k, value: v }));
    },
    missingRequiredFields() {
      if (!this.extractedData) return [];
      const required = [
        { key: 'businessName', label: 'Business name' },
        { key: 'businessAddress', label: 'Business address' },
        { key: 'ein', label: 'EIN/Tax ID' },
        { key: 'businessType', label: 'Business type' },
        { key: 'repName', label: 'Contact name' },
        { key: 'repTitle', label: 'Contact title' },
        { key: 'repEmail', label: 'Contact email' },
        { key: 'repPhone', label: 'Contact phone' },
      ];
      return required.filter(f => !this.extractedData[f.key]);
    },
    isCountryMissingFromDB() {
      return this.isSuperAdmin && !this.siteCountryFromDB;
    },
    countryDisplay() {
      const map = { US: 'United States', CA: 'Canada' };
      return map[this.siteCountry] || this.siteCountry || 'Not set';
    },
  },
  methods: {
    stripParentheticalPrefix(value) {
      if (!value) return value;
      return value.replace(/^\s*\([^)]+\)\s*:?\s*/g, '').trim();
    },
    normalizeBusinessType(v) {
      const t = v.toLowerCase();
      if (/llc|pllc/.test(t)) return 'Limited Liability Corporation';
      if (/corp|inc/.test(t)) return 'Corporation';
      if (/sole\s*proprietor/.test(t)) return 'Sole Proprietorship';
      if (/partner/.test(t)) return 'Partnership';
      if (/non.?profit/.test(t)) return 'Non-profit Corporation';
      return v;
    },
    extractFromEmail() {
      this.extracting = true;
      this.extractedData = null;
      try {
        const text = this.emailContent;
        const extract = (patterns) => {
          for (const p of patterns) {
            const m = text.match(p);
            if (m) return this.stripParentheticalPrefix(m[1].trim());
          }
          return '';
        };
        this.extractedData = {
          businessName: extract([
            /(?:legal\s*)?business\s*name[:\s]*([^\n]+)/i,
            /(?:company|practice)\s*name[:\s]*([^\n]+)/i,
            /(?:registered\s*as|dba)[:\s]*([^\n]+)/i,
          ]),
          ein: extract([
            /(?:ein|tax\s*id|employer\s*identification)[:\s#]*(\d{2}[-\s]?\d{7})/i,
            /(?:business\s*registration\s*number|bn)[:\s#]*(\d{2}[-\s]?\d{7})/i,
            /(\d{2}-\d{7})/,
            // Bare 9-digit form: clients often write "Tax ID: 852737704".
          ]).replace(/\s/g, '').replace(/^(\d{2})(\d{7})$/, '$1-$2'),
          businessType: (() => {
            const raw = extract([
              /(?:business|entity)\s*type[:\s]*([^\n,]+)/i,
              /type[:\s]*(llc|pllc|corporation|corp|inc|sole\s*proprietor|partnership|s\s*corp|c\s*corp)/i,
            ]);
            return raw ? this.normalizeBusinessType(raw) : '';
          })(),
          businessAddress: extract([
            // The optional second line lets a wrapped address ("Suite 400" on
            // its own line) come through. The negative lookahead is load-bearing:
            // without it, `[^\n:]+` happily ate the NEXT FIELD'S LABEL, so
            // "Address: …TX 76137\nTax ID: 852737704" extracted as
            // "…TX 76137, Tax ID" (Calvin Eastwood, 2026-08-26). Every client
            // email has a `Label:` line after the address, so this fired on
            // essentially every import. Requiring the continuation line to
            // contain NO colon keeps real wrapped addresses and drops labels.
            /(?:business\s*)?address[:\s]*([^\n]+(?:\n(?![^\n]*:)[^\n]+)?)/i,
            /(?:physical\s*)?location[:\s]*([^\n]+)/i,
          ]).replace(/\n/g, ', '),
          repPhone: extract([
            /(?:business\s*)?phone(?:\s*number)?[:\s]*([\d\s\-().]+)/i,
            /(?:tel|telephone)(?:\s*number)?[:\s]*([\d\s\-().]+)/i,
            /(\(\d{3}\)\s*\d{3}[-\s]?\d{4})/,
            /(\d{3}[.\-\s]\d{3}[.\-\s]\d{4})/,
          ]),
          repEmail: extract([
            /(?:contact|business|owner)?\s*email[:\s]*([^\s\n]+@[^\s\n]+)/i,
            /([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/i,
          ]),
          // Patterns are tried in array order, so the most specific label wins
          // regardless of where it sits in the email. The `m` flag is
          // load-bearing: without it `$` means end of the WHOLE pasted email,
          // so a name with no trailing comma never matched at all — the
          // extractor silently returned '' for "Business Owner: Calvin Eastwood"
          // and left the required field blank (2026-08-26). The original
          // single pattern only worked for the placeholder's comma-delimited
          // "Contact person: John Smith, Owner" shape.
          repName: extract([
            /business\s*owner[:\s]*([^,\n]+?)\s*(?:,|$)/im,
            /(?:contact\s*person|authorized\s*representative)[:\s]*([^,\n]+?)\s*(?:,|$)/im,
            /(?:owner|representative|contact)\s*name[:\s]*([^,\n]+?)\s*(?:,|$)/im,
            /(?:owner|representative|contact)[:\s]*([^,\n]+?)\s*(?:,|$)/im,
          ]),
          repTitle: extract([
            /(?:title|position|role)[:\s]*([^\n,]+)/i,
          ]),
          // Normalized to an absolute https URL — clients write bare hostnames
          // ("summerbrookdentalfw.com") but the Website field is validated as a
          // URL and is sent to Twilio as `businessWebsite`.
          website: (() => {
            const raw = extract([
              /(?:business\s*)?website[:\s]*(https?:\/\/[^\s\n]+|www\.[^\s\n]+|[a-z0-9.-]+\.[a-z]{2,})/i,
            ]);
            if (!raw) return '';
            return /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;
          })(),
          // Clients routinely send these two (our own request email asks for
          // them) but there were no patterns for them, so they were dropped and
          // had to be retyped in Business Info every time.
          privacyPolicyURL: extract([
            /privacy(?:\s*policy)?\s*(?:url|link|page)?[:\s]*(https?:\/\/[^\s\n]+)/i,
          ]),
          termsOfServiceURL: extract([
            /terms(?:\s*(?:of\s*(?:service|use)|and\s*conditions|&\s*conditions))?\s*(?:url|link|page)?[:\s]*(https?:\/\/[^\s\n]+)/i,
          ]),
        };
      } catch (e) {
        console.error('Extraction failed:', e);
      } finally {
        this.extracting = false;
      }
    },
    applyExtractedData() {
      if (!this.extractedData) return;
      this.$emit('import-data', this.extractedData);
      this.$emit('next');
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
.a2p-overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin: 28px 0 32px;
}
.a2p-overview-card {
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
  padding: 18px;
  transition: border-color 0.2s, transform 0.2s;
}
.a2p-overview-card:hover {
  border-color: var(--a2p-border-strong);
  transform: translateY(-1px);
}
.a2p-overview-card .ov-num {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: var(--a2p-fg-3);
  letter-spacing: 0.1em;
  font-weight: 600;
  margin-bottom: 8px;
}
.a2p-overview-card .ov-title {
  font-weight: 600;
  font-size: 15px;
  color: var(--a2p-fg);
  margin-bottom: 6px;
}
.a2p-overview-card .ov-desc {
  font-size: 13px;
  color: var(--a2p-fg-2);
  line-height: 1.5;
  margin-bottom: 14px;
  min-height: 40px;
}
.a2p-overview-card .ov-status {
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--a2p-fg-3);
}
</style>
